import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../supabase';
import TeamMemberModal from '../components/TeamMemberModal';
import api from '../api';
import useIsMobile from '../hooks/useIsMobile';


function RightPanel({ ws, isActive, onSwitch, onDeleted, currentUserId }) {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedMember, setExpandedMember] = useState(null);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviting, setInviting] = useState(false);
    const [inviteSuccess, setInviteSuccess] = useState(false);
    const [modalMember, setModalMember] = useState(null);
    const [modalTasks, setModalTasks] = useState([]);
    const [modalLoading, setModalLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    const isOwner = ws.owner_id === currentUserId;

    async function handleDeleteWorkspace() {
        if (deleteConfirm !== ws.name) return;
        setDeleting(true);
        setDeleteError('');
        try {
            await api.delete(`/workspaces/${ws.id}`);
            setShowDeleteModal(false);
            onDeleted(ws.id);
        } catch (err) {
            setDeleteError(err.response?.data?.error || 'Failed to delete workspace. Try again.');
        } finally {
            setDeleting(false);
        }
    }



    useEffect(() => {
        setLoading(true);
        setExpandedMember(null);
        api.get(`/workspaces/${ws.id}/members`)
            .then(res => { setMembers(res.data); setLoading(false); })
            .catch(() => setLoading(false));
    }, [ws.id]);

    async function handleInvite(e) {
        e.preventDefault();
        setInviting(true);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            await api.post(`/workspaces/${ws.id}/invite`, {
                email: inviteEmail,
                invitedBy: session?.user?.id,
                workspaceName: ws.name
            });
            setInviteEmail('');
            setInviteSuccess(true);
            setTimeout(() => setInviteSuccess(false), 3000);
        } catch (err) {
            console.error(err);
        } finally {
            setInviting(false);
        }
    }

    const avatarColors = [
        { bg: '#dbeafe', color: '#1d4ed8' },
        { bg: '#ede9fe', color: '#7c3aed' },
        { bg: '#fef3c7', color: '#92400e' },
        { bg: '#ffe4e6', color: '#be123c' },
        { bg: '#ccfbf1', color: '#0f766e' },
    ];

    return (
        <motion.div
            key={ws.id}
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.2 }}
            style={{
                background: 'var(--bg-card)',
                border: '0.5px solid var(--border)',
                borderRadius: 14, overflow: 'hidden',
                display: 'flex', flexDirection: 'column',
                minHeight: 400,
            }}
        >
            {/* Header */}
            <div style={{
                padding: '14px 20px', borderBottom: '0.5px solid var(--border)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                flexShrink: 0,
            }}>
                <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
                        {ws.name}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                            fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                            background: ws.role === 'manager' ? 'var(--accent-light)' : 'var(--blue-light)',
                            color: ws.role === 'manager' ? 'var(--accent-text)' : 'var(--blue)'
                        }}>
                            {ws.role}
                        </span>
                        {isActive && (
                            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Active workspace</span>
                        )}
                    </div>
                </div>
                {!isActive && (
                    <motion.button
                        whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                        onClick={() => onSwitch(ws)}
                        style={{
                            fontSize: 13, padding: '8px 18px', borderRadius: 8,
                            border: 'none', background: '#16a34a', color: '#fff',
                            cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit'
                        }}
                    >
                        Switch to this →
                    </motion.button>
                )}
            </div>

            {/* Stats row */}
            <div className="ws-stats-grid" style={{
                display: 'grid', gridTemplateColumns: 'repeat(5,1fr)',
                borderBottom: '0.5px solid var(--border)', flexShrink: 0,
            }}>
                {[
                    { label: 'Members', value: ws.stats?.members || 0, color: 'var(--text-primary)' },
                    { label: 'Total', value: ws.stats?.total || 0, color: 'var(--text-primary)' },
                    { label: 'Pending', value: ws.stats?.pending || 0, color: 'var(--amber)' },
                    { label: 'Overdue', value: ws.stats?.overdue || 0, color: 'var(--red)' },
                    { label: 'Done', value: ws.stats?.done || 0, color: 'var(--accent)' },
                ].map((s, i) => (
                    <div key={s.label} style={{
                        padding: '10px 0', textAlign: 'center',
                        borderRight: i < 4 ? '0.5px solid var(--border)' : 'none',
                        background: isActive ? 'var(--accent-light)' : 'var(--bg)',
                    }}>
                        <div style={{ fontSize: 18, fontWeight: 800, color: s.color, letterSpacing: -0.5, lineHeight: 1, marginBottom: 3 }}>
                            {s.value}
                        </div>
                        <div style={{ fontSize: 9, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {s.label}
                        </div>
                    </div>
                ))}
            </div>

            {/* Members list */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
                    Team members
                </div>

                {loading ? (
                    <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading...</div>
                ) : members.map((m, i) => {
                    const av = avatarColors[i % avatarColors.length];
                    const isOpen = expandedMember === m.id;
                    return (
                        <div key={m.id}>
                            <div
                                onClick={async () => {
                                    setModalMember(m);
                                    setModalTasks([]);
                                    setModalLoading(true);
                                    try {
                                        const res = await api.get('/commitments', {
                                            params: { workspaceId: ws.id, userId: m.user_id }
                                        });
                                        setModalTasks(res.data);
                                    } catch { setModalTasks([]); }
                                    finally { setModalLoading(false); }
                                }}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 10,
                                    padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                                    background: isOpen ? 'var(--bg)' : 'transparent',
                                    transition: 'background 0.1s', marginBottom: 2,
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                                onMouseLeave={e => { if (!isOpen) e.currentTarget.style.background = 'transparent'; }}
                            >
                                <div style={{
                                    width: 30, height: 30, borderRadius: '50%',
                                    background: av.bg, color: av.color,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 11, fontWeight: 800, flexShrink: 0
                                }}>
                                    {(m.name || m.email)?.charAt(0).toUpperCase()}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {m.name || m.email}
                                    </div>
                                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                                        Joined {new Date(m.joined_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                    </div>
                                </div>
                                <span style={{
                                    fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, flexShrink: 0,
                                    background: m.role === 'manager' ? 'var(--accent-light)' : 'var(--blue-light)',
                                    color: m.role === 'manager' ? 'var(--accent-text)' : 'var(--blue)'
                                }}>
                                    {m.role}
                                </span>
                                <motion.span
                                    animate={{ rotate: isOpen ? 90 : 0 }}
                                    transition={{ duration: 0.2 }}
                                    style={{ fontSize: 14, color: 'var(--text-muted)', flexShrink: 0, display: 'inline-block' }}
                                >
                                    ›
                                </motion.span>
                            </div>

                            <AnimatePresence>
                                {isOpen && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.2 }}
                                        style={{ overflow: 'hidden' }}
                                    >
                                        <div style={{
                                            background: 'var(--bg)', borderRadius: 8,
                                            padding: '10px 14px', margin: '3px 0 6px 40px',
                                            border: '0.5px solid var(--border)'
                                        }}>
                                            {[
                                                ...(m.name ? [['Name', m.name]] : []),
                                                ['Email', m.email],
                                                ['Role', m.role],
                                                ['Joined', new Date(m.joined_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })],
                                            ].map(([label, value]) => (
                                                <div key={label} style={{
                                                    display: 'flex', justifyContent: 'space-between',
                                                    padding: '4px 0', borderBottom: '0.5px solid var(--border)', fontSize: 12
                                                }}>
                                                    <span style={{ color: 'var(--text-muted)' }}>{label}</span>
                                                    <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{value}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    );
                })}
            </div>

            {/* Footer — invite + code — manager only */}
            {ws.role === 'manager' && (
                <div style={{
                    borderTop: '0.5px solid var(--border)', padding: '12px 20px',
                    display: 'flex', flexWrap: 'wrap', gap: 16,
                    alignItems: 'start', flexShrink: 0, background: 'var(--bg-card)'
                }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                            Invite member
                        </div>
                        <form onSubmit={handleInvite} style={{ display: 'flex', gap: 8 }}>
                            <input
                                className="field-input"
                                type="email"
                                placeholder="teammate@company.com"
                                value={inviteEmail}
                                onChange={e => setInviteEmail(e.target.value)}
                                required
                                style={{ marginBottom: 0, flex: 1 }}
                            />
                            <button className="btn-extract" type="submit" disabled={inviting}
                                style={{ whiteSpace: 'nowrap', padding: '0 16px' }}>
                                {inviting ? '...' : 'Send →'}
                            </button>
                        </form>
                        {inviteSuccess && (
                            <div style={{ fontSize: 12, color: 'var(--accent)', marginTop: 6 }}>✓ Invite sent!</div>
                        )}
                    </div>

                    {ws.code && (
                        <div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                                Team code
                            </div>
                            <div style={{
                                background: 'var(--bg)', border: '0.5px solid var(--border)',
                                borderRadius: 8, padding: '7px 14px', textAlign: 'center', minWidth: 110
                            }}>
                                <div style={{ fontSize: 15, fontWeight: 900, letterSpacing: 3, color: 'var(--accent-text)', lineHeight: 1 }}>
                                    {ws.code}
                                </div>
                                <div style={{ fontSize: 9, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 3 }}>
                                    Share with team
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
            {/* Danger zone — owner only */}
            {isOwner && (
                <div style={{
                    borderTop: '1px solid var(--border)',
                    padding: '14px 20px',
                    background: 'var(--bg)',
                    flexShrink: 0,
                }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--red)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>
                        ⚠ Danger Zone
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                        <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2 }}>
                                Delete this workspace
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                                Permanently removes all meetings, tasks and members. Cannot be undone.
                            </div>
                        </div>
                        <button
                            onClick={() => { setShowDeleteModal(true); setDeleteConfirm(''); setDeleteError(''); }}
                            style={{
                                fontSize: 12, padding: '7px 16px', borderRadius: 8,
                                border: '1px solid var(--red)',
                                background: 'transparent', color: 'var(--red)',
                                cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
                                flexShrink: 0,
                                transition: 'background 0.15s, color 0.15s',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = 'var(--red)'; e.currentTarget.style.color = '#fff'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--red)'; }}
                        >
                            Delete workspace
                        </button>
                    </div>
                </div>
            )}

            {/* Delete confirmation modal */}
            <AnimatePresence>
                {showDeleteModal && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            onClick={() => !deleting && setShowDeleteModal(false)}
                            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9000 }}
                        />
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 10 }}
                            transition={{ duration: 0.18 }}
                            style={{
                                position: 'fixed', top: '50%', left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: 'min(440px, calc(100vw - 32px))',
                                background: 'var(--bg-card)',
                                border: '1px solid var(--border)',
                                borderRadius: 16,
                                padding: 24,
                                zIndex: 9001,
                                boxShadow: '0 24px 64px rgba(0,0,0,0.25)',
                            }}
                        >
                            {/* Header */}
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 20 }}>
                                <div style={{
                                    width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                                    background: 'rgba(239,68,68,0.12)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
                                }}>⚠</div>
                                <div>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
                                        Delete "{ws.name}"?
                                    </div>
                                    <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                                        This will permanently delete:
                                    </div>
                                </div>
                            </div>

                            {/* What gets deleted */}
                            <div style={{
                                background: 'rgba(239,68,68,0.06)',
                                border: '1px solid rgba(239,68,68,0.2)',
                                borderRadius: 10, padding: '12px 14px', marginBottom: 20,
                            }}>
                                {[
                                    `All ${ws.stats?.total || 0} commitments and tasks`,
                                    `All meetings and transcripts`,
                                    `All ${ws.stats?.members || 0} member${ws.stats?.members !== 1 ? 's' : ''} will be removed`,
                                    `This action cannot be undone`,
                                ].map((item, i) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--red)', marginBottom: i < 3 ? 6 : 0, fontWeight: i === 3 ? 700 : 400 }}>
                                        <span style={{ flexShrink: 0 }}>✕</span> {item}
                                    </div>
                                ))}
                            </div>

                            {/* Name confirmation */}
                            <div style={{ marginBottom: 16 }}>
                                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                                    Type <strong style={{ color: 'var(--text-primary)' }}>{ws.name}</strong> to confirm:
                                </div>
                                <input
                                    autoFocus
                                    value={deleteConfirm}
                                    onChange={e => { setDeleteConfirm(e.target.value); setDeleteError(''); }}
                                    onKeyDown={e => e.key === 'Enter' && deleteConfirm === ws.name && handleDeleteWorkspace()}
                                    placeholder={ws.name}
                                    disabled={deleting}
                                    style={{
                                        width: '100%', padding: '10px 12px', borderRadius: 8, boxSizing: 'border-box',
                                        border: `1px solid ${deleteConfirm === ws.name ? 'var(--red)' : 'var(--border)'}`,
                                        background: 'var(--bg)', color: 'var(--text-primary)',
                                        fontSize: 13, fontFamily: 'inherit', outline: 'none',
                                        transition: 'border-color 0.15s',
                                    }}
                                />
                            </div>

                            {deleteError && (
                                <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 12 }}>
                                    {deleteError}
                                </div>
                            )}

                            {/* Actions */}
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button
                                    onClick={handleDeleteWorkspace}
                                    disabled={deleteConfirm !== ws.name || deleting}
                                    style={{
                                        flex: 1, padding: '10px', borderRadius: 8, border: 'none',
                                        background: deleteConfirm === ws.name && !deleting ? 'var(--red)' : 'var(--border)',
                                        color: deleteConfirm === ws.name && !deleting ? '#fff' : 'var(--text-muted)',
                                        fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
                                        cursor: deleteConfirm === ws.name && !deleting ? 'pointer' : 'default',
                                        transition: 'all 0.15s',
                                    }}
                                >
                                    {deleting ? '⏳ Deleting...' : 'Permanently Delete'}
                                </button>
                                <button
                                    onClick={() => setShowDeleteModal(false)}
                                    disabled={deleting}
                                    style={{
                                        padding: '10px 20px', borderRadius: 8,
                                        border: '1px solid var(--border)',
                                        background: 'transparent', color: 'var(--text-muted)',
                                        fontSize: 13, fontFamily: 'inherit', cursor: 'pointer',
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            <TeamMemberModal
                member={modalMember}
                tasks={modalTasks}
                loading={modalLoading}
                onClose={() => setModalMember(null)}
            />
        </motion.div>
    );
}

const wsStyles = `
    @media (max-width: 767px) {
        .ws-stats-grid { grid-template-columns: repeat(3, 1fr) !important; }
        .ws-stats-grid > div:nth-child(3) { border-right: none !important; }
        .ws-left-panel { max-height: 280px; overflow-y: auto; }
    }
`;

export default function Workspace() {
    const [workspaces, setWorkspaces] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [currentUserId, setCurrentUserId] = useState(null);
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const activeWsId = localStorage.getItem('workspaceId');

    const fetchWorkspaces = useCallback(async () => {
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) return;
            setCurrentUserId(session.user.id);

            const { data: wsData } = await api.get(`/workspaces?userId=${session.user.id}`);

            const withStats = await Promise.all(wsData.map(async (ws) => {
                try {
                    const [membersRes, commitmentsRes] = await Promise.all([
                        api.get(`/workspaces/${ws.id}/members`),
                        api.get(`/commitments`, { params: { workspaceId: ws.id } })
                    ]);
                    const commitments = commitmentsRes.data;
                    const now = new Date();
                    return {
                        ...ws,
                        stats: {
                            members: membersRes.data.length,
                            total: commitments.length,
                            pending: commitments.filter(c => c.status === 'pending').length,
                            overdue: commitments.filter(c => {
                                if (c.status === 'completed') return false;
                                if (c.deadline) { const [y,m,d] = c.deadline.slice(0,10).split('-').map(Number); const due = new Date(y,m-1,d); return !isNaN(due) && due < now; }
                                return false;
                            }).length,
                            done: commitments.filter(c => c.status === 'completed').length,
                        }
                    };
                } catch {
                    return { ...ws, stats: { members: 0, total: 0, pending: 0, overdue: 0, done: 0 } };
                }
            }));

            setWorkspaces(withStats);
            // Auto select active workspace
            const active = withStats.find(w => w.id === activeWsId) || withStats[0];
            if (active) setSelected(active);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [activeWsId]);

    useEffect(() => { fetchWorkspaces(); }, [fetchWorkspaces]);

    function switchWorkspace(ws) {
        localStorage.setItem('workspaceId', ws.id);
        localStorage.setItem('workspaceName', ws.name);
        localStorage.setItem('userRole', ws.role);
        localStorage.removeItem('soloMode');
        window.dispatchEvent(new Event('workspaceSwitched'));
        navigate('/dashboard');
    }

    function handleWorkspaceDeleted(deletedId) {
        const remaining = workspaces.filter(w => w.id !== deletedId);
        setWorkspaces(remaining);

        // If this was the active workspace, switch to another or go solo
        if (activeWsId === deletedId) {
            if (remaining.length > 0) {
                const next = remaining[0];
                localStorage.setItem('workspaceId', next.id);
                localStorage.setItem('workspaceName', next.name);
                localStorage.setItem('userRole', next.role);
                localStorage.removeItem('soloMode');
                window.dispatchEvent(new Event('workspaceSwitched'));
                setSelected(next);
            } else {
                localStorage.removeItem('workspaceId');
                localStorage.removeItem('workspaceName');
                localStorage.setItem('soloMode', 'true');
                localStorage.setItem('userRole', 'solo');
                window.dispatchEvent(new Event('workspaceSwitched'));
                setSelected(null);
                navigate('/dashboard');
            }
        } else {
            // Just deselect if not active
            setSelected(remaining.length > 0 ? remaining[0] : null);
        }
    }

    return (
        <div className="main">
            <style>{wsStyles}</style>
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                style={{ marginBottom: 20, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}
            >
                <div>
                    <div className="page-title">My Teams</div>
                    <div className="page-sub">
                        {workspaces.length} workspace{workspaces.length !== 1 ? 's' : ''} · Click to view details
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <motion.button
                        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                        onClick={() => navigate('/create-workspace')}
                        style={{
                            fontSize: 12, padding: '7px 14px', borderRadius: 8,
                            border: 'none', background: '#16a34a', color: '#fff',
                            cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit'
                        }}
                    >
                        + Create team
                    </motion.button>
                    <motion.button
                        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                        onClick={() => navigate('/enter-invite')}
                        style={{
                            fontSize: 12, padding: '7px 14px', borderRadius: 8,
                            border: '1px solid var(--border)', background: 'transparent',
                            color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 500, fontFamily: 'inherit'
                        }}
                    >
                        Join another team
                    </motion.button>
                </div>
            </motion.div>

            {loading ? (
                <div className="empty-state"><div className="empty-title">Loading...</div></div>
            ) : workspaces.length === 0 ? (
                <div className="empty-state" style={{ marginTop: 48 }}>
                    <div className="empty-title">No workspaces yet</div>
                    <div className="empty-sub">Create a team or join one with an invite code.</div>
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '240px 1fr', gap: 12, height: isMobile ? 'auto' : 520 }}>
                    {/* Left panel */}
                    <div className="ws-left-panel" style={{
                        background: 'var(--bg-card)', border: '0.5px solid var(--border)',
                        borderRadius: 14, overflow: 'hidden', display: 'flex', flexDirection: 'column'
                    }}>
                        <div style={{
                            padding: '10px 14px', borderBottom: '0.5px solid var(--border)',
                            fontSize: 11, fontWeight: 600, color: 'var(--text-muted)',
                            textTransform: 'uppercase', letterSpacing: '0.05em'
                        }}>
                            Workspaces
                        </div>

                        {workspaces.map(ws => {
                            const isActive = ws.id === activeWsId;
                            const isSelected = selected?.id === ws.id;
                            return (
                                <div
                                    key={ws.id}
                                    onClick={() => setSelected(ws)}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: 10,
                                        padding: '10px 14px', cursor: 'pointer',
                                        borderBottom: '0.5px solid var(--border)',
                                        background: isSelected ? 'var(--accent-light)' : 'transparent',
                                        transition: 'background 0.1s',
                                    }}
                                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'var(--bg)'; }}
                                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                                >
                                    <div style={{
                                        width: 34, height: 34, borderRadius: 9, flexShrink: 0,
                                        background: ws.role === 'manager' ? 'var(--accent-light)' : 'var(--blue-light)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16
                                    }}>
                                        {ws.role === 'manager' ? '🏢' : '👤'}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{
                                            fontSize: 13, fontWeight: 600, marginBottom: 3,
                                            color: isSelected ? 'var(--accent-text)' : 'var(--text-primary)',
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                        }}>
                                            {ws.name}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                            <span style={{
                                                fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 20,
                                                background: ws.role === 'manager' ? 'var(--accent-light)' : 'var(--blue-light)',
                                                color: ws.role === 'manager' ? 'var(--accent-text)' : 'var(--blue)'
                                            }}>
                                                {ws.role}
                                            </span>
                                            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{ws.stats?.members || 0} members</span>
                                        </div>
                                    </div>
                                    {isActive && (
                                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#16a34a', flexShrink: 0 }} />
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Right panel */}
                    {selected && (
                        <RightPanel
                            ws={selected}
                            isActive={selected.id === activeWsId}
                            onSwitch={switchWorkspace}
                            onDeleted={handleWorkspaceDeleted}
                            currentUserId={currentUserId}
                        />
                    )}
                </div>
            )}
        </div>
    );
}